/**
 * Utilities for handling translation files
 */
import { TranslationObject } from '../types';

// Flatten a nested JSON object into a flat object with dot notation keys
export const flattenObject = (obj: TranslationObject, prefix: string = ''): Record<string, string> => {
  return Object.keys(obj).reduce((acc: Record<string, string>, key: string) => {
    const pre = prefix.length ? `${prefix}.` : '';
    
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      Object.assign(acc, flattenObject(obj[key] as TranslationObject, `${pre}${key}`));
    } else {
      acc[`${pre}${key}`] = obj[key] as string;
    }
    
    return acc;
  }, {});
};

// Unflatten a flat object with dot notation keys into a nested JSON object
export const unflattenObject = (obj: Record<string, string>): TranslationObject => {
  const result: TranslationObject = {};
  
  for (const key in obj) {
    const keys = key.split('.');
    let current: any = result;
    
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      if (i === keys.length - 1) {
        current[k] = obj[key];
      } else {
        current[k] = current[k] || {};
        current = current[k];
      }
    }
  }
  
  return result;
};

// Find missing keys between master and translation file
export const findMissingKeys = (master: TranslationObject, translation: TranslationObject): string[] => {
  const masterFlat = flattenObject(master);
  const translationFlat = flattenObject(translation);
  
  // Missing keys are those that exist in the master but don't exist at all in the translation
  return Object.keys(masterFlat).filter(key => !(key in translationFlat));
};

// Find empty values (keys exist but have empty values)
export const findEmptyValues = (translation: TranslationObject): string[] => {
  const flat = flattenObject(translation);
  
  // Empty values are those that exist in the translation but are empty
  return Object.keys(flat).filter(key => 
    flat[key] === '' || 
    flat[key] === null || 
    flat[key] === undefined
  );
};

// Create updated translation with missing keys from master
export const addMissingKeys = (master: TranslationObject, translation: TranslationObject): TranslationObject => {
  const masterFlat = flattenObject(master);
  const translationFlat = flattenObject(translation);
  const updatedFlat: Record<string, string> = { ...translationFlat };
  
  // Add missing keys with empty values (only if they don't exist at all)
  Object.keys(masterFlat).forEach(key => {
    if (!(key in translationFlat)) {
      updatedFlat[key] = '';
    }
  });
  
  return unflattenObject(updatedFlat);
};

// Copy a key's value from master to translation
export const copyFromMaster = (master: TranslationObject, translation: TranslationObject, key: string): TranslationObject => {
  const masterFlat = flattenObject(master);
  const translationFlat = flattenObject(translation);
  
  if (masterFlat[key]) {
    translationFlat[key] = masterFlat[key];
  }
  
  return unflattenObject(translationFlat);
};

// Search in keys and values
export const searchTranslation = (translation: TranslationObject, searchTerm: string): string[] => {
  if (!searchTerm) return [];
  
  const flat = flattenObject(translation);
  const lowerSearchTerm = searchTerm.toLowerCase();
  
  return Object.keys(flat).filter(key => 
    key.toLowerCase().includes(lowerSearchTerm) || 
    (flat[key] && flat[key].toLowerCase().includes(lowerSearchTerm))
  );
};

// Find keys in translation that don't exist in master
export const findNonMasterKeys = (master: TranslationObject, translation: TranslationObject): string[] => {
  const masterFlat = flattenObject(master);
  const translationFlat = flattenObject(translation);
  
  // Return keys that exist in translation but not in master
  return Object.keys(translationFlat).filter(key => !(key in masterFlat));
};

// Remove keys from a translation object
export const removeKeys = (translation: TranslationObject, keysToRemove: string[]): TranslationObject => {
  const flatTranslation = flattenObject(translation);
  const updatedFlat: Record<string, string> = { ...flatTranslation };
  
  // Remove each key
  keysToRemove.forEach(key => {
    delete updatedFlat[key];
  });
  
  // Return the updated object with the nested structure restored
  return unflattenObject(updatedFlat);
};